import {
  KeyIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
  BanknotesIcon,
  QueueListIcon,
  TruckIcon,
  DocumentTextIcon,
  DocumentPlusIcon,
  FolderOpenIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../Auth/Auth';
import { Link, Navigate } from 'react-router-dom';
import BreadCrumb from '../BreadCrumb';
import MoonLoader from 'react-spinners/MoonLoader';

const pages = [
  { name: 'Administration', to: '/admin', component: Link, current: true },
];

const actions = [
  {
    title: 'RBAC',
    to: '/admin/rbac',
    desc: "Role Based Access Control",
    icon: KeyIcon,
    iconForeground: 'text-white',
    iconBackground: 'bg-gradient-to-br from-slate-500 to-gray-600',
  },
  {
    title: 'Employees',
    to: '/admin/employees',
    desc: "Manage Employee Access",
    icon: UserGroupIcon,
    iconForeground: 'text-white',
    iconBackground: 'bg-gradient-to-br from-blue-500 to-cyan-600',
  },
  {
    title: 'Contractors',
    to: '/admin/contractors',
    desc: "Register and Manage Technicians",
    icon: WrenchScrewdriverIcon,
    iconForeground: 'text-white',
    iconBackground: 'bg-gradient-to-br from-orange-500 to-amber-600',
  },
  {
    title: 'Referral Agents',
    to: '/admin/agents',
    desc: "Manage Commission Agents",
    icon: BanknotesIcon,
    iconForeground: 'text-white',
    iconBackground: 'bg-gradient-to-br from-emerald-500 to-teal-600',
  },
  {
    title: 'Manage Services',
    to: '/admin/services',
    desc: "Manage Products and Services Offered",
    icon: QueueListIcon,
    iconForeground: 'text-white',
    iconBackground: 'bg-gradient-to-br from-purple-500 to-indigo-600',
  },
  {
    title: 'Manage Suppliers',
    to: '/procurement/suppliers',
    desc: "Add, Update or Delete Supplier Contact Details",
    icon: TruckIcon,
    iconForeground: 'text-white',
    iconBackground: 'bg-gradient-to-br from-rose-500 to-pink-600',
  },
  {
    title: 'Supplier Payment Status',
    to: '/procurement/procurementstatus',
    desc: "Update Payment Status of Supplied Items",
    icon: DocumentTextIcon,
    iconForeground: 'text-white',
    iconBackground: 'bg-gradient-to-br from-violet-500 to-purple-600',
  },
  {
    title: 'Create Help Article',
    to: '/admin/newarticle',
    desc: "Create a new Article for the Helpcenter",
    icon: DocumentPlusIcon,
    iconForeground: 'text-white',
    iconBackground: 'bg-gradient-to-br from-lime-500 to-green-600',
  },
  {
    title: 'All Articles',
    to: '/admin/allarticles',
    desc: "List of All Articles",
    icon: FolderOpenIcon,
    iconForeground: 'text-white',
    iconBackground: 'bg-gradient-to-br from-yellow-500 to-orange-600',
  },
  {
    title: 'Event Logs',
    to: '/admin/eventlogs',
    desc: "View diagnostic warnings and error logs",
    icon: ExclamationTriangleIcon,
    iconForeground: 'text-white',
    iconBackground: 'bg-gradient-to-br from-red-500 to-rose-600',
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const Admin = () => {
  const { role, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-gray-50">
        <div className="text-center">
          <MoonLoader size={60} color="#64748B" />
          <p className="mt-4 text-sm font-medium text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (role !== 'Admin') {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50 py-4">
      <BreadCrumb pages={pages} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-slate-500 to-gray-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Cog6ToothIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Administration</h1>
                <p className="text-sm text-gray-600 mt-1">Manage system settings, users, and configurations</p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {actions.map((action) => (
            <Link
              key={action.title}
              to={action.to}
              className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
            >
              {/* Gradient Background Overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-transparent to-gray-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

              {/* Card Content */}
              <div className="relative p-8">
                {/* Icon Container */}
                <div className="flex items-center justify-center mb-6">
                  <div
                    className={classNames(
                      action.iconBackground,
                      'rounded-2xl p-4 shadow-lg transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3'
                    )}
                  >
                    <action.icon className={classNames(action.iconForeground, "h-8 w-8")} aria-hidden="true" />
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-center text-gray-900 mb-3 group-hover:text-slate-600 transition-colors duration-200">
                  {action.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-center text-gray-600 leading-relaxed">
                  {action.desc}
                </p>

                {/* Arrow Icon */}
                <div className="absolute bottom-6 right-6 text-gray-400 group-hover:text-slate-600 transition-colors duration-200">
                  <svg className="h-6 w-6 transform group-hover:translate-x-1 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>
              </div>

              {/* Bottom Accent Bar */}
              <div className={classNames(
                action.iconBackground,
                "absolute bottom-0 left-0 right-0 h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"
              )}></div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Admin;